import type { Image } from "@graphql/generated/components";
import { getImageAltTag } from "@lib/utils/attributesUtils";
import type { ImageWithVideo } from "@pages/product/[productName]/[articleNumber]/[colorCode]";
import { NextImage } from "@ui/components/media-and-icons/image/NextImage";
import { Carousel, CarouselProductImage } from "@ui/components/navigation/carousel/Carousel";
import { memo, type FC } from "react";
import { sizes, space } from "ui/src/design-tokens";
import type { ReactPlayerProps } from "react-player";
import dynamic from "next/dynamic";
import { Box } from "@ui/components/layout/box/Box";
import ProductInteractiveVideo from "./ProductInteractiveVideo";

export interface ProductImagesCarouselProps {
    images: Image[] | ImageWithVideo[];
    onImageClick: (imageIndex: number) => void;
    imageBackgroundColor: string;
    altTagPattern?: string;
    videoContainerRef?: React.RefObject<HTMLDivElement>;
    playerRef?: ReactPlayerProps;
}

const carouselWidth = "48px !important";

const ProductVideo = dynamic(
    () => import("@components/commerce/product/image/ProductVideo").then((mod) => mod.ProductVideo),
    {
        ssr: false,
    }
);

export const ProductImagesCarousel: FC<ProductImagesCarouselProps> = memo(
    ({ images, onImageClick, imageBackgroundColor, altTagPattern }) => {
        return (
            <CarouselProductImage
                sx={{
                    display: ["block", "block", "block", "none"],
                    py: 0,
                    mt: ["-18", "-19"],
                    pt: ["18", "19"],
                    bg: "#eeeeee",
                }}
                carouselIndicators={false}
                iconSize={{
                    height: `${sizes.icon.md} !important`,
                    width: `${sizes.icon.md} !important`,
                }}
                opacity={1}
                containerStyle={{
                    px: space[4],
                    top: 0,
                    right: 0,
                    bottom: 0,
                    justifyContent: "space-between",
                }}
                navsx={{
                    flexContainer: {
                        position: "relative",
                    },
                    leftButton: {
                        position: "absolute",
                        marginTop: "auto",
                        marginBottom: "auto",
                        top: "-35vw",
                        left: ["-14vw", "calc(-85vw + 24px)", "calc(-91vw + 24px)"],
                        background: "transparent",
                        width: carouselWidth,
                        height: carouselWidth,
                    },
                    rightButton: {
                        position: "absolute",
                        marginTop: "auto",
                        marginBottom: "auto",
                        top: "-35vw",
                        left: ["calc(80vw - 24px)", "50px"],
                        background: "transparent",
                        width: carouselWidth,
                        height: carouselWidth,
                    },
                }}
                hasPagination
                paginationsx={{
                    pb: 4,
                    display: {
                        base: "flex",
                        lg: "none",
                    },
                }}
            >
                {images.map((image, index) => {
                    if (image.video720) {
                        return (
                            <Box
                                aspectRatio={4 / 5}
                                key={image.url}
                                onClickCapture={(ev) => {
                                    ev.preventDefault();
                                    onImageClick(index);
                                }}
                            >
                                <ProductInteractiveVideo
                                    url={image.url}
                                    height="100%"
                                    shouldRotateOnDrag={false}
                                />
                            </Box>
                        );
                    } else if (image.video) {
                        return (
                            <Box
                                aspectRatio={4 / 5}
                                key={image.url}
                                onClick={() => onImageClick(index)}
                            >
                                <ProductVideo productVideo={image.url} />
                            </Box>
                        );
                    } else {
                        return (
                            <NextImage
                                key={image.url}
                                src={image.url}
                                alt={getImageAltTag(altTagPattern, image.label)}
                                pointerEvents="auto"
                                sizes={["100vw", "100vw", "100vw", "100vw", "1vw"]}
                                onClick={() => onImageClick(index)}
                                backgroundColor={imageBackgroundColor}
                            />
                        );
                    }
                })}
            </CarouselProductImage>
        );
    }
);

ProductImagesCarousel.displayName = "ProductImagesCarousel";

export default Carousel;
